import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { login } from '../services/api';
import { useUser } from '../contexts/UserContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Eye, EyeOff, Mail, Lock } from 'lucide-react';
import Logo from '../components/ui/Logo';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { refreshUser } = useUser();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Clear any existing tokens/cache before login attempt
      localStorage.removeItem('token');
      localStorage.removeItem('userCache');
      sessionStorage.clear();

      const response = await login(email.trim(), password, rememberMe);
      localStorage.setItem('token', response.data.data.accessToken);
      
      // Refresh user data in context
      await refreshUser();
      
      navigate('/projects');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background-primary p-4 relative overflow-hidden">
      {/* Background gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-transparent to-purple-500/10" />
      
      {/* Background elements */}
      <div className="absolute top-20 left-20 w-72 h-72 bg-blue-500/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-20 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl" />
      
      <div className="w-full max-w-md relative z-10">
        <Card variant="glass" className="backdrop-blur-xl bg-white/5 border border-white/10 shadow-2xl">
          <CardHeader className="space-y-4 text-center">
            <div className="flex justify-center">
              <Logo size={64} />
            </div>
            <div>
              <CardTitle className="text-3xl font-bold text-foreground-primary">
                Welcome Back
              </CardTitle>
              <CardDescription className="text-foreground-tertiary mt-2">
                Sign in to your ProjectHub account
              </CardDescription>
            </div>
          </CardHeader>
          
          <CardContent className="space-y-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-4 text-sm text-status-error bg-status-error/10 border border-status-error/20 rounded-lg backdrop-blur-sm">
                  {error}
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="email" className="text-foreground-secondary">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-foreground-tertiary" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="pl-10 border-white/30 placeholder:text-muted-foreground focus:border-blue-400 focus:ring-2 focus:ring-blue-500/30"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password" className="text-foreground-secondary">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-foreground-tertiary" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="pl-10 pr-10 border-white/30 placeholder:text-muted-foreground focus:border-blue-400 focus:ring-2 focus:ring-blue-500/30"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-foreground-tertiary hover:text-foreground-secondary"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <input
                    id="rememberMe"
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="w-4 h-4 text-blue-500 bg-transparent border border-white/30 rounded focus:ring-blue-500 focus:ring-2"
                  />
                  <Label htmlFor="rememberMe" className="text-sm text-foreground-secondary cursor-pointer">
                    Lembrar-me por 30 dias
                  </Label>
                </div>
                <Link
                  to="/forgot-password"
                  className="text-sm text-blue-400 hover:text-blue-300 hover:underline"
                >
                  Esqueceu a senha?
                </Link>
              </div>

              <Button
                type="submit"
                loading={loading}
                loadingText="Entrando..."
                variant="gradient"
                size="lg"
                className="w-full"
              >
                Entrar
              </Button>
            </form>

            <div className="text-center text-sm text-foreground-tertiary">
              Don't have an account?{' '}
              <Link 
                to="/register" 
                className="font-medium text-blue-400 hover:text-blue-300 underline-offset-4 hover:underline"
              >
                Create account
              </Link>
            </div>
          </CardContent>
        </Card>
        
        {/* Decorative elements */}
        <div className="absolute -top-4 -left-4 w-8 h-8 bg-blue-500/20 rounded border border-blue-500/30" />
        <div className="absolute -bottom-4 -right-4 w-6 h-6 bg-purple-500/20 rounded border border-purple-500/30" />
      </div>
    </div>
  );
};

export default Login;